import React, { useEffect, useRef, useState } from "react";

import { themevals } from "theme/themevals";
import { getLead, getLeadsTable } from "api/Leads";

export interface PickedLead {
  id: string;
  name: string;
  contactName: string | null;
  contactEmail: string | null;
}

/**
 * Search-as-you-type lead picker (2026-09-28). Replaces the plain <select>
 * of the first 100 leads by name on the proposal builder and the proposal
 * page's "Attach to a lead": 212+ leads do not fit a dropdown, several
 * contacts at one organization all read "Cadence Education", and a lead
 * past the first 100 could not be picked at all.
 *
 * Server-side search (organization, contact name, email). Each result
 * reads organization, then the PERSON - the same shape as the leads list.
 * With nothing typed it suggests leads matching `suggestFor` (usually the
 * prospect name), so the likely lead is one click away.
 */
export default function LeadPicker({
  value,
  onChange,
  suggestFor,
  placeholder = "Search leads by organization, contact or email",
  autoFocus,
}: {
  value: string | null;
  onChange: (lead: PickedLead | null) => void;
  suggestFor?: string;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [picked, setPicked] = useState<PickedLead | null>(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PickedLead[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Show who is picked (value can arrive from a URL or a reopened draft).
  useEffect(() => {
    if (!value) return setPicked(null);
    if (picked?.id === value) return;
    getLead(value).then((l: any) => {
      if (l && !l.error) setPicked({ id: l.id, name: l.name, contactName: l.contactName ?? null, contactEmail: l.contactEmail ?? null });
    });
  }, [value]); // eslint-disable-line

  const term = q.trim() || (suggestFor ?? "").trim();
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setLoading(true);
      getLeadsTable({ query: { status: "all", search: term || undefined, recordsPer: 20, pageNum: 1, sortBy: "name", sortDir: "asc" } }).then((res: any) => {
        setLoading(false);
        const list = Array.isArray(res?.items) ? res.items : [];
        setItems(list.map((l: any) => ({ id: l.id, name: l.name, contactName: l.contactName ?? null, contactEmail: l.contactEmail ?? null })));
      });
    }, 250);
    return () => clearTimeout(t);
  }, [open, term]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const choose = (l: PickedLead | null) => {
    setPicked(l);
    setOpen(false);
    setQ("");
    onChange(l);
  };

  const box: React.CSSProperties = { border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 6, background: "#fff", fontFamily: "Poppins", fontSize: 13 };

  if (picked && value) {
    return (
      <div style={{ ...box, display: "flex", alignItems: "center", gap: 10, padding: "8px 12px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: "#000" }}>{picked.name}</div>
          <div style={{ fontSize: 12, color: "#1a2332" }}>
            {picked.contactName || "No contact name"}
            {picked.contactEmail ? <span style={{ color: themevals.colors.dkgrey }}>{` \u00b7 ${picked.contactEmail}`}</span> : null}
          </div>
        </div>
        <button type="button" onClick={() => { setOpen(true); setPicked(null); onChange(null); }} style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", padding: 0, fontFamily: "Poppins", fontSize: 12, fontWeight: 500 }}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        value={q}
        autoFocus={autoFocus}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && items[0]) { e.preventDefault(); choose(items[0]); }
          if (e.key === "Escape") setOpen(false);
        }}
        placeholder={placeholder}
        style={{ ...box, width: "100%", boxSizing: "border-box", padding: "8px 12px" }}
      />
      {open && (
        <div style={{ ...box, position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, maxHeight: 320, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.12)" }}>
          {!q.trim() && suggestFor?.trim() && (
            <div style={{ padding: "6px 12px", fontSize: 11, color: "#818181", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>
              {`Matching \u201c${suggestFor.trim()}\u201d`}
            </div>
          )}
          {loading && items.length === 0 && <div style={{ padding: "10px 12px", color: themevals.colors.dkgrey }}>{"Searching\u2026"}</div>}
          {!loading && items.length === 0 && <div style={{ padding: "10px 12px", color: themevals.colors.dkgrey }}>No leads match.</div>}
          {items.map((l) => (
            <div
              key={l.id}
              onMouseDown={(e) => { e.preventDefault(); choose(l); }}
              style={{ padding: "8px 12px", borderTop: "1px solid #F1F1F1", cursor: "pointer" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#F5F8FC")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div style={{ fontWeight: 600, color: "#000" }}>{l.name}</div>
              <div style={{ fontSize: 12, color: "#1a2332" }}>
                {l.contactName || "No contact name"}
                {l.contactEmail ? <span style={{ color: themevals.colors.dkgrey }}>{` \u00b7 ${l.contactEmail}`}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
